/**
 * SessionStart Hook Handler — cache warm + navigation-index summary injection.
 *
 * Executed on session new/resume/clear/compact events. Warms the navigation-index
 * cache and returns a rendered summary as additionalContext.
 *
 * Latency constraint: NO AST analysis or ContextPack generation.
 */

import { renderNavigationIndexPrompt } from "../task-runner.js";
import {
  ensureNavigationIndex,
  type EnsureNavigationIndexOptions,
} from "./ensure-navigation-index.js";
import {
  buildContextResponse,
  type HookOutput,
  type SessionStartInput,
} from "./hook-protocol.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SessionStartHandlerConfig {
  /** Absolute path to .claude/cache/ directory. */
  cacheDir: string;
  /** Source files for cache invalidation (component.yaml, agent-contracts.yaml, etc.). */
  sourceFiles: string[];
  /** Options for buildNavigationContext(). */
  navigationOptions: EnsureNavigationIndexOptions["navigationOptions"];
}

// ---------------------------------------------------------------------------
// Handler
// ---------------------------------------------------------------------------

/**
 * Create a SessionStart hook handler.
 */
export function createSessionStartHandler(config: SessionStartHandlerConfig) {
  return async function handleSessionStart(
    _input: SessionStartInput,
  ): Promise<HookOutput> {
    const result = await ensureNavigationIndex({
      cacheDir: config.cacheDir,
      sourceFiles: config.sourceFiles,
      navigationOptions: config.navigationOptions,
    });

    const summary = renderNavigationIndexPrompt(result.navigationIndex);
    const cacheNote = result.fromCache
      ? "(loaded from cache)"
      : "(freshly built)";

    const additionalContext = [
      summary,
      "",
      `Navigation index ${cacheNote}. Use analyzer MCP tools for deep investigation.`,
    ].join("\n");

    return buildContextResponse("SessionStart", additionalContext);
  };
}
